import { SearchBarILS } from "@components/SearchBar";
import { LoanListEntry } from "@modules/Loan/backoffice/LoanList/LoanListEntry";
import SearchResultsList from "@modules/SearchControls/SearchResultsList";
import PropTypes from "prop-types";
import React, { Component } from "react";
import _isEmpty from "lodash/isEmpty";
import { Header, Segment, Message, Loader } from "semantic-ui-react";
import history from "@history";
import { BackOfficeRoutes } from "@routes/urls";
import { userApi } from "@api/users/user";
import { AuditService } from "@api/audit/audit";
import { invenioConfig } from "@config";
import { loanApi } from "@api/loans";

export default class ItemsSearch extends Component {
  constructor(props) {
    super(props);
    this.searchBarRef = React.createRef();
    this.state = {
      pendingBarcode: null,
      showConfirmation: false,
      isCheckingOverdue: false,
      showOverdueMessage: false,
      overdueInfo: null,
    };
  }

  logCheckInAudit = async (barcode) => {
    const response = await userApi.get();
    const user = response?.data || response;

    const userName = user.full_name || "Unknown";
    const userRole =
      Array.isArray(user.roles) && user.roles.length > 0
        ? user.roles[0]
        : "Unknown";

    // Fetch the loan using loanApi to get patronName
    const loanQuery = loanApi
      .query()
      .withState(invenioConfig.CIRCULATION.loanActiveStates)
      .withItemBarcode(barcode)
      .qs();

    const loanResponse = await loanApi.list(loanQuery);
    let patronName = "Unknown";

    if (
      loanResponse?.data?.hits &&
      loanResponse.data.hits.length > 0 &&
      loanResponse.data.hits[0].metadata?.patron?.name
    ) {
      patronName = loanResponse.data.hits[0].metadata.patron.name;
    }

    const details = `${userName} checked in the book checked out by ${patronName} with barcode ${barcode} at ${new Date().toLocaleString()}.`;

    await AuditService.api.createLog({
      action: "Check-In Item",
      details,
      created_by: `${userName} Role : (${userRole})`,
    });
  };

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  checkOverdueStatus = async (barcode) => {
    const { checkOverdue } = this.props;

    if (!checkOverdue) {
      return { isOverdue: false };
    }

    try {
      return await checkOverdue(barcode);
    } catch (error) {
      return { isOverdue: false };
    }
  };

  redirectToFeeManagement = (loanId, patronId, patronName) => {
    // Use patron name instead of ID for better filtering
    const feeManagementUrl = `${
      BackOfficeRoutes.fineManagement
    }?patron=${patronId}&patronName=${encodeURIComponent(
      patronName
    )}&loan=${loanId}`;
    history.push(feeManagementUrl);
  };

  clearSearchBar = () => {
    if (this.searchBarRef.current) {
      const searchBarComponent =
        this.searchBarRef.current.querySelector("input") ||
        this.searchBarRef.current.querySelector('[data-testid="search-input"]');
      if (searchBarComponent) {
        searchBarComponent.value = "";
      }
    }
  };

  handleBarcodeInput = async (queryString, event) => {
    const { checkin, searchForCheckIn } = this.props;
    const { pendingBarcode, showConfirmation } = this.state;

    if (event && event.key === "Enter" && showConfirmation && pendingBarcode) {
      await checkin(pendingBarcode, async () => {
        await this.logCheckInAudit(
          pendingBarcode,
          this.state.overdueInfo || {}
        );

        this.setState({
          pendingBarcode: null,
          showConfirmation: false,
        });
        this.clearSearchBar();
      });
      return;
    }

    if (
      showConfirmation &&
      (queryString.trim() === "" || queryString.trim() === pendingBarcode)
    ) {
      await checkin(pendingBarcode, async () => {
        await this.logCheckInAudit(
          pendingBarcode,
          this.state.overdueInfo || {}
        );

        this.setState({
          pendingBarcode: null,
          showConfirmation: false,
        });
        this.clearSearchBar();
      });
      return;
    }

    if (queryString.trim() === "") return;

    const barcode = queryString.trim();
    this.setState({ isCheckingOverdue: true });

    try {
      const overdueResult = await this.checkOverdueStatus(barcode);

      if (overdueResult.isOverdue) {
        this.setState({
          isCheckingOverdue: false,
          showOverdueMessage: true,
          overdueInfo: overdueResult,
        });

        setTimeout(() => {
          this.redirectToFeeManagement(
            overdueResult.loanId,
            overdueResult.patronId,
            overdueResult.patronName // Add patron name here
          );
          this.clearSearchBar();
          this.setState({
            showOverdueMessage: false,
            overdueInfo: null,
          });
        }, 4000);

        return;
      }

      this.setState({
        pendingBarcode: barcode,
        showConfirmation: true,
        isCheckingOverdue: false,
      });

      if (searchForCheckIn) {
        await searchForCheckIn(barcode);
      }
    } catch (error) {
      this.setState({ isCheckingOverdue: false });
    }
  };

  handleKeyDown = (event) => {
    const { showConfirmation, pendingBarcode } = this.state;

    if (event.key === "Enter" && showConfirmation && pendingBarcode) {
      event.preventDefault();
      this.handleBarcodeInput("", event);
    }

    if (event.key === "Escape" && showConfirmation) {
      this.setState({
        pendingBarcode: null,
        showConfirmation: false,
      });
      this.clearSearchBar();
    }
  };

  render() {
    const {
      pendingBarcode,
      showConfirmation,
      isCheckingOverdue,
      showOverdueMessage,
      overdueInfo,
    } = this.state;
    const { loans } = this.props;

    return (
      <>
        <div ref={this.searchBarRef}>
          <SearchBarILS
            onSearchHandler={this.handleBarcodeInput}
            placeholder={
              isCheckingOverdue
                ? "Checking overdue status..."
                : showConfirmation
                ? "Press Enter to confirm check-in (Esc to cancel)..."
                : "Scan physical copy barcode to check-in..."
            }
            disabled={isCheckingOverdue}
          />
        </div>

        {isCheckingOverdue && (
          <Segment>
            <Message info>
              <Message.Header>Checking Status</Message.Header>
              <Loader active inline size="mini" />
              <p>Verifying if item has overdue fines...</p>
            </Message>
          </Segment>
        )}

        {showOverdueMessage && overdueInfo && (
          <Segment color="orange">
            <Message warning>
              <Message.Header>Overdue Item Detected</Message.Header>
              <p>
                This item is overdue! Patron:{" "}
                <strong>{overdueInfo.patronName || "Unknown"}</strong>
                <br />
                <Loader active inline size="mini" />
                <em> Redirecting to fee management...</em>
              </p>
            </Message>
          </Segment>
        )}

        {showConfirmation &&
          pendingBarcode &&
          !isCheckingOverdue &&
          !showOverdueMessage && (
            <Segment color="blue">
              <Message info>
                <Message.Header>Ready to Check-in</Message.Header>
                <p>
                  Barcode: <strong>{pendingBarcode}</strong>
                  <br />
                  <em>
                    Press <kbd>Enter</kbd> to confirm or <kbd>Esc</kbd> to
                    cancel
                  </em>
                </p>
              </Message>
            </Segment>
          )}

        {!_isEmpty(loans) && (
          <>
            <Header as="h3">Choose the loan to check-in:</Header>
            <SearchResultsList
              results={loans.hits}
              ListEntryElement={LoanListEntry}
              target="_blank"
            />
          </>
        )}
      </>
    );
  }
}

ItemsSearch.propTypes = {
  loans: PropTypes.object,
  checkin: PropTypes.func.isRequired,
  searchForCheckIn: PropTypes.func,
  checkOverdue: PropTypes.func,
};

ItemsSearch.defaultProps = {
  loans: null,
  searchForCheckIn: null,
  checkOverdue: null,
};
